'use client';

import { Link } from '@/lib/router-events/patch-router/link';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Locale } from '@/i18n';
import { getSeoContent } from '@/utils/getSeoContent';
import { formatMessage } from '@/utils/translationHelper';
import Icon from '@/components/shared/Icons';
import { getSimpleClickFact } from '@/analytics/helpers/cventAnalytics/getSimpleClickFact';
import type { SimpleClickFact } from '@/analytics/models/cventAnalytics/SimpleClickFact';
import useMap from '@/hooks/useMap';
import useAnalytics from '@/hooks/useAnalytics';
import type { Filter } from '@cvent/venue-search-shared/types';
import { useBreakpoints } from '@/hooks/useBreakpoints';
import type { AssociatedLocation } from '@/models/AssociatedLocation';

const EACH_CHAR_WIDTH = 10;
const PADDING = 70;

export type SeoSectionStrings = {
  description: string;
  similarLocations: string;
  urlTitle: string;
  showMore: string;
  showLess: string;
};

type Props = {
  searchTerm: string;
  locale: Locale;
  associatedLocation: AssociatedLocation[];
  seoSectionStrings: SeoSectionStrings;
  filters: Filter;
};

// Function to calculate the number of columns based on parent width
function getCols(parentWidth: number, associatedLocation: AssociatedLocation[]): number {
  const largestLocLength = associatedLocation.reduce(
    (maxLength, currentLocation) => Math.max(maxLength, currentLocation.location.length),
    0
  );

  const tileSize = EACH_CHAR_WIDTH * largestLocLength + PADDING;

  // Calculate number of columns that can fit within the parent width
  const cols = Math.floor(parentWidth / tileSize);
  if (cols > associatedLocation.length) {
    return associatedLocation.length;
  }
  return cols;
}

function getURLTitle(locale: Locale, urlText: string, loc: string): string {
  return formatMessage(urlText, locale, {
    location: loc,
    br: (chunks: ReactNode[]) => (
      <>
        <br />
        {chunks}
      </>
    ),
    p: (chunks: ReactNode[]) => <p className="leading-4 whitespace-nowrap font-bold">{chunks}</p>
  });
}

export default function SeoSection({
  searchTerm,
  locale,
  associatedLocation,
  seoSectionStrings,
  filters
}: Props): JSX.Element {
  // State to manage if the content is expanded or not
  const [isExpanded, setIsExpanded] = useState(false);
  // State to manage the number of columns in associated locations grid
  const [cols, setCols] = useState(1);
  const { isMobile } = useBreakpoints();
  const { mapView } = useMap();
  const analytics = useAnalytics();
  const parentRef = useRef<HTMLDivElement>(null); // Create a ref for the parent container
  const description = useMemo(
    () => getSeoContent(locale, seoSectionStrings.description),
    [locale, seoSectionStrings.description]
  );

  const updateCols = useCallback(() => {
    if (parentRef.current && associatedLocation.length > 0) {
      const parentWidth = parentRef.current.offsetWidth; // Get parent width
      // Check if screen width is mobile
      const columns = isMobile ? 1 : getCols(parentWidth, associatedLocation); // Calculate columns

      if (columns !== cols) {
        setCols(columns);
      }
    }
  }, [cols, isMobile, associatedLocation]);

  useEffect(() => {
    updateCols(); // Initial call to set columns
    window.addEventListener('resize', updateCols); // Adjust columns on resize

    return () => {
      window.removeEventListener('resize', updateCols);
    };
  }, [associatedLocation, updateCols]);

  // Function to toggle the expanded state
  const toggleExpansion = useCallback(() => {
    setIsExpanded(!isExpanded);
  }, [isExpanded]);

  const triggerSimpleClickFact = useCallback(
    async (location: string) => {
      const simpleClickFact = getSimpleClickFact(
        'On Page Content',
        `Nearby Location - ${location}`,
        'null',
        'null',
        mapView,
        searchTerm,
        'null',
        'null',
        JSON.stringify(filters)
      );
      analytics.reportFact<SimpleClickFact>(simpleClickFact);
    },
    [mapView, searchTerm, filters, analytics]
  );

  return (
    <div className="w-full rounded-lg border border-neutral-30 bg-background p-6 font-primary tracking-wide">
      <div
        className={`relative overflow-hidden transition-all duration-300 ${
          isExpanded || !description ? 'max-h-full' : 'max-h-48'
        }`}
        // Fading effect
        style={{
          background:
            isExpanded || !description
              ? 'none'
              : 'linear-gradient(180deg, rgba(255, 255, 255, 0) 75%, rgba(255, 255, 255, 0.83) 80%, #FFF 100%)',
          WebkitMaskImage:
            isExpanded || !description
              ? 'none'
              : 'linear-gradient(180deg, rgba(0, 0, 0, 1) 75%, rgba(0, 0, 0, 1) 80%, rgba(0, 0, 0, 0) 100%)',
          maskImage:
            isExpanded || !description
              ? 'none'
              : 'linear-gradient(180deg, rgba(0, 0, 0, 1) 75%, rgba(0, 0, 0, 1) 80%, rgba(0, 0, 0, 0) 100%)'
        }}
      >
        <div
          className="flex flex-col gap-6"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: description }}
        />
        <div className="my-6 mr-4 border border-neutral-30" />

        {associatedLocation.length > 0 && (
          <div className="flex flex-col gap-1" ref={parentRef}>
            <h2 className="text-[18px] font-normal text-neutral-90 md:text-d-xs/relaxed">
              {seoSectionStrings.similarLocations}
            </h2>
            <div
              className="my-4 grid gap-4 "
              style={{
                gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`
              }}
            >
              {associatedLocation.map(associatedLoc => (
                <Link
                  type="link"
                  aria-label={associatedLoc.location}
                  key={associatedLoc.id}
                  className="cursor-pointer"
                  href={associatedLoc.locationUrl}
                  onClick={() => triggerSimpleClickFact(associatedLoc.location)}
                >
                  <div className="flex flex-col items-center rounded-xl border border-neutral-30 bg-neutral-10 px-4 py-3 font-primary text-b-xs hover:border-interactive-70 hover:bg-interactive-10 hover:text-interactive-70 md:text-b-md">
                    {getURLTitle(locale, seoSectionStrings.urlTitle, associatedLoc.location)}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Show More / Show Less button */}
      <div className="my-2 flex justify-center font-primary">
        <button
          type="button"
          aria-label={isExpanded ? seoSectionStrings.showLess : seoSectionStrings.showMore}
          onClick={toggleExpansion}
          className="flex items-center fill-brand-60 text-b-lg text-brand-60"
        >
          {isExpanded ? seoSectionStrings.showLess : seoSectionStrings.showMore}
          {isExpanded ? (
            <Icon id="chevron-up" className="ml-1.5 h-4 w-4 " />
          ) : (
            <Icon id="chevron-down" className="ml-1.5 h-4 w-4 " />
          )}
        </button>
      </div>
    </div>
  );
}
